<template>
  <div class="money-transfers-page features-money-transfers-wrapper">
    <BlocksServiceHeroNew
      service="MoneyTransfers"
      :hero="cmsData.hero[0]"
      :country-slug="countrySlug"
      :breadcrumbs="breadcrumbs"
      @scroll-to-checkout="scrollToMiniCheckout"
    />
    <BlocksContentLayout
      v-if="cmsData.content"
      :content="cmsData.content"
    />
  </div>
</template>
<script setup>

import { reactive, computed } from 'vue'
import { applicationDataStore } from '@/store/application/data'
import { CMS_SERVICE_HERO_QUERY, DYNAMIC_CONTENT_QUERY } from '@/utils/graphql-queries'
import { SANCTIONED_REMITTANCE_COUNTRIES } from '@/utils/constants'
import request from '@/api/dato/datohelper'
const route = useRoute()
const { $messages, $mRouter, $bus, $track } = useNuxtApp()
const currentLocale = useLocale() || 'en'
const countrySlug = route.params.country
const dataStore = applicationDataStore()
const { setLocale } = useI18n()

definePageMeta({
  layout: 'default',
  stopDefaultSwitch: true,
  ref: 'money-transfers'
})

const data = reactive({
  country: null,
  moneyTransfersServicePage: null,
  defaultMoneyTransfersServicePage: null,
  unsupportedMoneyTransfersServicePage: null
})

data.country = dataStore.getCountryBySlug(countrySlug)

if (!data.country) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

const query = `query MyQuery {
      moneyTransfersServicePage(locale: ${currentLocale}, filter: {country: {eq: "${data.country.id}"}}) {
        ${CMS_SERVICE_HERO_QUERY}
        ${DYNAMIC_CONTENT_QUERY}
      }
      defaultMoneyTransfersServicePage(locale: ${currentLocale}) {
        ${CMS_SERVICE_HERO_QUERY}
        ${DYNAMIC_CONTENT_QUERY}
      }
      unsupportedMoneyTransfersServicePage(locale: ${currentLocale}) {
        ${CMS_SERVICE_HERO_QUERY}
        ${DYNAMIC_CONTENT_QUERY}
      }
    }`

const response = await request(query, 'MoneyTransfersDynamic' + data.country.slug + currentLocale)
if (!response || (!response.moneyTransfersServicePage && !response.defaultMoneyTransfersServicePage && !response.unsupportedMoneyTransfersServicePage)) {
  // throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

data.moneyTransfersServicePage = response.moneyTransfersServicePage
data.defaultMoneyTransfersServicePage = response.defaultMoneyTransfersServicePage
data.unsupportedMoneyTransfersServicePage = response.unsupportedMoneyTransfersServicePage

const isCountrySanctioned = computed(() => {
  return SANCTIONED_REMITTANCE_COUNTRIES.includes(data.country.id)
})

const cmsData = computed(() => {
  if (data.moneyTransfersServicePage) {
    // otherwise, if the current country has a custom entry in Dato, use it
    return data.moneyTransfersServicePage
  }

  if (data.country.hasRemittance && data.defaultMoneyTransfersServicePage) {
    // or otherwise use the default
    const parsed = parseContentCountryName(data.defaultMoneyTransfersServicePage)
    return parsed
  }

  if ((!data.country.hasRemittance || isCountrySanctioned) && data.unsupportedMoneyTransfersServicePage) {
    // if the country has a bespoke MTU Service Page Hero, use it
    const parsed = parseContentCountryName(data.unsupportedMoneyTransfersServicePage)
    return parsed
  }

  // hopefully the below will never happen, it's only here because of computed functions needing a return statement
  return null
})

const breadcrumbs = computed(() => {
  return [
    {
      title: $messages('label.home'),
      to: $mRouter.getLocalRoute({ name: 'index' })
    },
    {
      title: $messages('page_labels.money_transfers'),
      to: $mRouter.getLocalRoute({ name: 'money-transfers' })
    },
    {
      title: $messages(`countries.${data.country.id}`)
    }
  ]
})

const parseContentCountryName = (page) => {
  const parsedContent = []
  if (page.content) {
    for (let i = 0; i < page.content.length; i++) {
      const block = page.content[i]
      if (block.title) {
        block.title = block.title.replace(/{{country}}/g, data.country.name)
      }
      if (block.body) {
        block.body = block.body.replace(/{{country}}/g, data.country.name)
      }
      parsedContent.push(block)
    }
  }
  page.content = parsedContent
  return page
}

const scrollToMiniCheckout = () => {
  const miniCheckout = document.querySelector('#mini-checkout-form')
  if (miniCheckout) {
    miniCheckout.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    })
  }
}

const handleLocaleSwitchHere = (val) => {
  const newcountry = dataStore.getCountryBySlug(val.currentRoute.params.country)
  const countrySlug = newcountry.alternativeSlugs[val.destinationLocale]
  setLocale(val.destinationLocale).then(() => {
    if (countrySlug) {
      const newroute = $mRouter.getLocalRoute({ name: 'country-money-transfers', params: { country: countrySlug } })
      window.location.href = newroute.href
    } else {
      window.location.href = $mRouter.getLocalRoute({ name: 'money-transfers' }).href
    }
  })
}

$bus.onDynamicRouteChangeLanguage(route.meta.key, handleLocaleSwitchHere)

onMounted(() => {
  $track.pageView(route, {
    id: `Money Transfers - ${data.country.name}`,
    name: data.country.name,
    component: `/${route.params.country}/money-transfers/`
  })
})

</script>
